import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { ResumeCitizenGroupActivitiesIndicatorB4Data } from "@/services/esus/indicator/esb/b4/getResumeCitizensGroupActivitiesIndicatorB4";
import { Paginated } from "@/@types/generics/paginated";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorEsb } from "@/utils/indicatorsEsb";
import {
	DetailedCitizenGroupActivitiesIndicatorB4,
	getDetailedCitizenGroupActivitiesIndicatorB4,
} from "@/services/esus/indicator/esb/b4/getDetailedCitizenGroupActivitiesIndicatorB4";

import { IndicatorEsbB4GroupActivitiesFilters } from "./IndicatorEsbB4GroupActivitiesFilters";
import { IndicatorEsbB4GroupActivitiesTable } from "./IndicatorEsbB4GroupActivitiesTable";
import { IndicatorEsbB4CitizenHistory } from "./IndicatorEsbB4CitizenHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB4GroupActivitiesProps = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedGroupActivities: Paginated<ResumeCitizenGroupActivitiesIndicatorB4Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeCitizensGroupActivitiesIndicatorB4: () => Promise<
		ResumeCitizenGroupActivitiesIndicatorB4Data[]
	>;
};

export function IndicatorEsbB4GroupActivities({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedGroupActivities,
	pageNumber,
	setPageNumber,
	handleGetAllResumeCitizensGroupActivitiesIndicatorB4,
}: Readonly<IndicatorEsbB4GroupActivitiesProps>) {
	const { uf, ibgeCode } = useCurrentAccount();
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithGroupActivities, setCitizenWithGroupActivities] =
		useState<DetailedCitizenGroupActivitiesIndicatorB4>(
			new DetailedCitizenGroupActivitiesIndicatorB4()
		);
	const [isLoadingCitizenWithGroupActivities, setIsLoadingCitizenWithGroupActivities] =
		useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithGroupActivities(citizenId: number, ine?: string) {
		setIsLoadingCitizenWithGroupActivities(true);
		setCitizenWithGroupActivities(
			await getDetailedCitizenGroupActivitiesIndicatorB4({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne || ine,
				citizenId,
			})
		);
		setIsLoadingCitizenWithGroupActivities(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const groupActivities = await handleGetAllResumeCitizensGroupActivitiesIndicatorB4();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(groupActivities).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorB4GroupActivitiesToGenericObjectArray(groupActivities);
			exportExcel(
				excelData,
				`Atividades coletivas - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB4GroupActivitiesToGenericObjectArray(
		indicatorB4GroupActivities: ResumeCitizenGroupActivitiesIndicatorB4Data[]
	) {
		return indicatorB4GroupActivities.map(
			(citizenWithGroupActivities) =>
				({
					Nome: citizenWithGroupActivities.fullName,
					DN: citizenWithGroupActivities.birthdate
						? dayjs(citizenWithGroupActivities.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithGroupActivities.birthdate
						? getAge(citizenWithGroupActivities.birthdate)
						: "",
					Documento:
						citizenWithGroupActivities.cpf ?? citizenWithGroupActivities.cns ?? "",
					Area: citizenWithGroupActivities.area ?? "FA",
					"Equipe ESF Cidadão": citizenWithGroupActivities.healthTeamName ?? "",
					"Equipe ESF Ine Cidadão": citizenWithGroupActivities.healthTeamIne ?? "",
					"Equipe ESB Atendimento": citizenWithGroupActivities.esbHealthTeamName ?? "",
					"Equipe ESB Ine Atendimento": citizenWithGroupActivities.esbHealthTeamIne ?? "",
					"Equipe ESF (vínculada a ESB atendimento)":
						citizenWithGroupActivities.linkedHealthTeamName ?? "",
					"Equipe ESF Ine (vínculada a ESB atendimento)":
						citizenWithGroupActivities.linkedHealthTeamIne ?? "",
					"Status escovação supervisionada": getCitizenScoreStatusDescriptionIndicatorEsb(
						citizenWithGroupActivities.supervisedBrushingStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen
					loading={isLoadingCitizenWithGroupActivities || isLoadingExportData}
				/>
				<Card>
					<Card.Header>
						<IndicatorEsbB4GroupActivitiesFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB4GroupActivitiesTable
							citizensWithGroupActivities={paginatedGroupActivities.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithGroupActivities}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedGroupActivities?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedGroupActivities.pageSize}
								totalPages={paginatedGroupActivities.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atividades Coletivas de Escovação Supervisionada no
								período
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsbB4CitizenHistory
							citizenWithGroupActivities={citizenWithGroupActivities}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
